<script setup>
/**
 * Transactions that look like the same payment written down twice: the same
 * payment type, amounts within a set share of each other (to the cent unless
 * loosened), descriptions at least half alike, within a week of each other.
 * See `lib/duplicates.js` for the rule.
 *
 * Nothing is removed on its own. Each pair is shown side by side and either
 * half can be deleted — two taps, like every delete here. Pairs are worked out
 * from the ledger live, so deleting one half makes the pair (and any other pair
 * that row was in) disappear.
 */
import { computed, ref, watch } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import { fmt } from '../../stores'
import { closeSheet, DEFAULT_SETTINGS, state, toast } from '../../stores/state'
import { setSetting } from '../../stores/settings'
import { t } from '../../stores/i18n'
import { allTransactions, deleteTransaction } from '../../stores/transactions'
import { fromDb } from '../../stores/live'
import { findDuplicates } from '../../lib/duplicates'
import { shortDay } from '../../lib/dates'

/**
 * The three knobs, as drafts: the fields keep exactly what is typed, and the
 * search uses the last value that made sense, so clearing a field on the way
 * to a new number does not flash every row in the ledger as a pair.
 */
const LIMITS = {
  duplicateSimilarity: { min: 0, max: 100 },
  duplicateDays: { min: 0, max: 365 },
  duplicateAmount: { min: 0, max: 100 },
}
const draft = ref({
  duplicateSimilarity: String(state.settings.duplicateSimilarity ?? DEFAULT_SETTINGS.duplicateSimilarity),
  duplicateDays: String(state.settings.duplicateDays ?? DEFAULT_SETTINGS.duplicateDays),
  duplicateAmount: String(state.settings.duplicateAmount ?? DEFAULT_SETTINGS.duplicateAmount),
})
const valid = (key) => {
  const text = String(draft.value[key]).trim()
  const value = Number(text)
  const { min, max } = LIMITS[key]
  return text !== '' && Number.isInteger(value) && value >= min && value <= max ? value : null
}
/** Saved as they settle, so the sheet opens next time where it was left. */
for (const key of Object.keys(LIMITS)) {
  watch(
    () => valid(key),
    (value) => value !== null && value !== state.settings[key] && setSetting(key, value),
  )
}
const similarityPercent = computed(
  () => valid('duplicateSimilarity') ?? state.settings.duplicateSimilarity ?? DEFAULT_SETTINGS.duplicateSimilarity,
)
const withinDays = computed(
  () => valid('duplicateDays') ?? state.settings.duplicateDays ?? DEFAULT_SETTINGS.duplicateDays,
)
const amountPercent = computed(
  () => valid('duplicateAmount') ?? state.settings.duplicateAmount ?? DEFAULT_SETTINGS.duplicateAmount,
)

/**
 * Every transaction, read while the sheet is open: a duplicate can be any two
 * rows, so this is one of the few questions that looks at all of them. Read
 * again after a delete here, like everything else the database answers.
 */
const ledger = fromDb(() => ({}), () => allTransactions(), null)

const pairs = computed(() =>
  findDuplicates(ledger.value ?? [], {
    withinDays: withinDays.value,
    minSimilarity: similarityPercent.value / 100,
    amountApart: amountPercent.value / 100,
  }),
)

/** The id primed for deletion; tapping anything else disarms it. */
const armed = ref('')
const busy = ref(false)

async function destroy(id) {
  if (armed.value !== id) {
    armed.value = id
    return
  }
  busy.value = true
  try {
    await deleteTransaction(id)
    toast(t('txSheet.deleted'))
  } finally {
    armed.value = ''
    busy.value = false
  }
}

const percent = (score) => `${Math.round(score * 100)}%`
/** A difference under 1% keeps a decimal: "0%" apart would read as the same amount. */
const apartText = (share) => (share < 0.01 ? `${(share * 100).toFixed(1)}%` : percent(share))
const pairKey = (pair) => `${pair.a.id}|${pair.b.id}`
</script>

<template>
  <Sheet
    large
    :title="t('duplicates.title')"
    :subtitle="t('duplicates.subtitle')"
    @close="closeSheet"
  >
    <!-- Two to a row on a phone, all three in one from `sm`. -->
    <div class="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
      <div>
        <label class="label" for="dup-similarity">{{ t('duplicates.similarity') }}</label>
        <div class="relative">
          <input
            id="dup-similarity"
            v-model="draft.duplicateSimilarity"
            type="number"
            inputmode="numeric"
            min="0"
            max="100"
            step="5"
            class="input tnum pr-8"
            :class="valid('duplicateSimilarity') === null && '!border-danger'"
            :aria-invalid="valid('duplicateSimilarity') === null"
          />
          <span class="pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2 text-faint">%</span>
        </div>
      </div>
      <div>
        <label class="label" for="dup-days">{{ t('duplicates.days') }}</label>
        <input
          id="dup-days"
          v-model="draft.duplicateDays"
          type="number"
          inputmode="numeric"
          min="0"
          max="365"
          step="1"
          class="input tnum"
          :class="valid('duplicateDays') === null && '!border-danger'"
          :aria-invalid="valid('duplicateDays') === null"
        />
      </div>
      <div>
        <label class="label" for="dup-amount">{{ t('duplicates.amountApart') }}</label>
        <div class="relative">
          <input
            id="dup-amount"
            v-model="draft.duplicateAmount"
            type="number"
            inputmode="numeric"
            min="0"
            max="100"
            step="1"
            class="input tnum pr-8"
            :class="valid('duplicateAmount') === null && '!border-danger'"
            :aria-invalid="valid('duplicateAmount') === null"
          />
          <span class="pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2 text-faint">%</span>
        </div>
      </div>
      <p class="col-span-full -mt-1 text-[12px] text-faint">{{ t('duplicates.configHint') }} {{ t('duplicates.configRange') }}</p>
    </div>

    <div v-if="pairs.length" class="grid gap-2.5">
      <p class="text-[12.5px] text-muted">{{ t('duplicates.found', { count: pairs.length }) }}</p>

      <div
        v-for="pair in pairs"
        :key="pairKey(pair)"
        class="min-w-0 rounded-xl border border-line-soft bg-raised/40 px-3 py-2.5"
        data-duplicate-pair
      >
        <p class="mb-1.5 text-[11.5px] text-faint">
          {{ t('duplicates.daysApart', { count: pair.days }) }} ·
          {{ t('duplicates.alike', { percent: percent(pair.similarity) }) }}
          <!-- Only when there is a difference to speak of: 0% is the usual case. -->
          <span v-if="pair.amountApart > 0" class="text-warn" data-amount-apart>
            · {{ t('duplicates.amountsDiffer', { percent: apartText(pair.amountApart) }) }}
          </span>
          <template v-if="pair.a.payment_type"> · {{ pair.a.payment_type }}</template>
        </p>

        <div
          v-for="tx in [pair.a, pair.b]"
          :key="tx.id"
          class="flex items-center gap-2 py-1"
        >
          <span class="w-[6.5ch] shrink-0 whitespace-nowrap text-[11.5px] text-faint">{{ shortDay(tx.date) }}</span>
          <span class="min-w-0 flex-1 truncate text-[13.5px]" :title="tx.description">
            {{ tx.description || '—' }}
          </span>
          <span class="tnum shrink-0 text-[13px] font-semibold" :class="tx.amount > 0 ? 'text-accent' : ''">
            {{ fmt(tx.amount, { signed: true }) }}
          </span>
          <button
            class="btn-ghost shrink-0 !px-2 !py-1 !text-[12px]"
            :disabled="busy"
            :aria-label="t('common.deleteThing', { name: tx.description || shortDay(tx.date) })"
            @click="destroy(tx.id)"
          >
            <Icon name="trash" :size="14" class="text-danger" />
            <span v-if="armed === tx.id" class="text-danger">{{ t('common.confirmDelete') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Not "none found" before anything has been looked at. -->
    <p v-else-if="!ledger" class="py-8 text-center text-[13px] text-muted" data-loading>{{ t('ledger.loading') }}</p>

    <div v-else class="py-8 text-center">
      <Icon name="check" :size="22" class="mx-auto text-accent" />
      <p class="mt-3 text-[14px] font-medium">{{ t('duplicates.none') }}</p>
      <p class="mt-1 text-[12.5px] text-faint">{{ t('duplicates.noneHint') }}</p>
    </div>

    <template #footer>
      <div class="flex-1" />
      <button class="btn-soft" @click="closeSheet">{{ t('common.done') }}</button>
    </template>
  </Sheet>
</template>
